@layer overridable {
    .root video:not(:has(source)) {
        // assumption that covers the most common video aspect ratio in
        // situations where the video hasn't loaded yet
        aspect-ratio: 16 / 9;
    }
}

.root {
    --color: var(--white);

    position: relative;
    width: 100%;
    height: 100%;

    video {
        display: block;

        &:not(:has(source)) {
            // so you can see the spinner
            background-color: var(--black);
        }
    }
}

.controlBar {
    composes: outline from "../../styles/composable/utility.module.css";
    display: flex;
    position: absolute;
    z-index: 10;
    gap: var(--spacer-8);

    [data-design-version="v2024"] & {
        top: var(--spacer-40);
        right: var(--spacer-40);
    }

    [data-design-version="v2026"] & {
        bottom: var(--spacer-24);
        right: var(--spacer-24);
    }
}

.autoPlayClickable {
    width: 48px;
    height: 48px;
}

.autoPlaySvg {
    fill: var(--color);

    [data-design-version="v2024"] & {
        width: 40px;
        height: 40px;
        border: 2px solid var(--color);
        border-radius: var(--border-radius-full);
    }

    [data-design-version="v2026"] & {
        width: 100%;
        height: 100%;
    }
}

.muteControl {
    composes: autoPlayClickable;
    padding: 12px;

    [data-design-version="v2024"] & {
        display: none;
    }
}

.playControl {
    composes: autoPlayClickable;
}

// The button is the container - making the whole video a hitbox
.centeredPlayButtonContainer {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    inset: 0;
    z-index: 10;

    // therefore we want the outline to act a little differently than normal
    &:focus {
        outline: none;

        & .centeredPlayButton {
            outline: var(--outline);
        }
    }
}

.centeredPlayButton {
    width: 48px;
    height: 48px;
    border: 1px solid var(--color);
    border-radius: var(--border-radius-full);
    transition: background-color 0.3s;
    fill: var(--color);

    [data-design-version="v2026"] & {
        background-color: var(--overlay-off-white-20);
        border-color: var(--off-white);
        backdrop-filter: blur(3px);

        &:hover {
            background-color: rgb(from var(--off-white) r g b / 50%);
        }
    }
}

.centeredPlayIcon {
    width: 100%;
    height: 100%;
    fill: var(--white);
}

.loaderOverlay {
    position: absolute;
    inset: 0;
    background-color: rgb(var(--white-base) / 30%);
    mix-blend-mode: difference;
    transition: opacity 0.2s;
    z-index: 1;
}

.loaderContainer {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;
    z-index: 2;
}

.loader {
    --plus: 1.8em;
    --x: 1.25em;
    --plus-negative: calc(var(--plus) * -1);
    --x-negative: calc(var(--x) * -1);
    --logo-color: var(--white-base);
    --base-opacity: rgb(var(--logo-color) / 20%);
    --full-opacity: rgb(var(--logo-color) / 100%);
    --fading-opacity-1: rgb(var(--logo-color) / 70%);
    --fading-opacity-2: rgb(var(--logo-color) / 50%);
    --dot-1: 0 var(--plus-negative) 0 0;
    --dot-2: var(--x) var(--x-negative) 0 0;
    --dot-3: var(--plus) 0 0 0;
    --dot-4: var(--x) var(--x) 0 0;
    --dot-5: 0 var(--plus) 0 0;
    --dot-6: var(--x-negative) var(--x) 0 0;
    --dot-7: var(--plus-negative) 0 0 0;
    --dot-8: var(--x-negative) var(--x-negative) 0 0;

    font-size: var(--spacer-16);
    width: 1em;
    height: 1em;
    background-color: var(--base-opacity);
    border-radius: 50%;
    text-indent: -9999em;
    animation: shadow-opacity 1.1s infinite ease;
    transform: translateZ(0);
}

@keyframes shadow-opacity {
    0%,
    100% {
        box-shadow:
            var(--dot-1) var(--full-opacity),
            var(--dot-2) var(--base-opacity),
            var(--dot-3) var(--base-opacity),
            var(--dot-4) var(--base-opacity),
            var(--dot-5) var(--base-opacity),
            var(--dot-6) var(--base-opacity),
            var(--dot-7) var(--fading-opacity-2),
            var(--dot-8) var(--fading-opacity-1);
    }

    12.5% {
        box-shadow:
            var(--dot-1) var(--fading-opacity-1),
            var(--dot-2) var(--full-opacity),
            var(--dot-3) var(--base-opacity),
            var(--dot-4) var(--base-opacity),
            var(--dot-5) var(--base-opacity),
            var(--dot-6) var(--base-opacity),
            var(--dot-7) var(--base-opacity),
            var(--dot-8) var(--fading-opacity-2);
    }

    25% {
        box-shadow:
            var(--dot-1) var(--fading-opacity-2),
            var(--dot-2) var(--fading-opacity-1),
            var(--dot-3) var(--full-opacity),
            var(--dot-4) var(--base-opacity),
            var(--dot-5) var(--base-opacity),
            var(--dot-6) var(--base-opacity),
            var(--dot-7) var(--base-opacity),
            var(--dot-8) var(--base-opacity);
    }

    37.5% {
        box-shadow:
            var(--dot-1) var(--base-opacity),
            var(--dot-2) var(--fading-opacity-2),
            var(--dot-3) var(--fading-opacity-1),
            var(--dot-4) var(--full-opacity),
            var(--dot-5) var(--base-opacity),
            var(--dot-6) var(--base-opacity),
            var(--dot-7) var(--base-opacity),
            var(--dot-8) var(--base-opacity);
    }

    50% {
        box-shadow:
            var(--dot-1) var(--base-opacity),
            var(--dot-2) var(--base-opacity),
            var(--dot-3) var(--fading-opacity-2),
            var(--dot-4) var(--fading-opacity-1),
            var(--dot-5) var(--full-opacity),
            var(--dot-6) var(--base-opacity),
            var(--dot-7) var(--base-opacity),
            var(--dot-8) var(--base-opacity);
    }

    62.5% {
        box-shadow:
            var(--dot-1) var(--base-opacity),
            var(--dot-2) var(--base-opacity),
            var(--dot-3) var(--base-opacity),
            var(--dot-4) var(--fading-opacity-2),
            var(--dot-5) var(--fading-opacity-1),
            var(--dot-6) var(--full-opacity),
            var(--dot-7) var(--base-opacity),
            var(--dot-8) var(--base-opacity);
    }

    75% {
        box-shadow:
            var(--dot-1) var(--base-opacity),
            var(--dot-2) var(--base-opacity),
            var(--dot-3) var(--base-opacity),
            var(--dot-4) var(--base-opacity),
            var(--dot-5) var(--fading-opacity-2),
            var(--dot-6) var(--fading-opacity-1),
            var(--dot-7) var(--full-opacity),
            var(--dot-8) var(--base-opacity);
    }

    87.5% {
        box-shadow:
            var(--dot-1) var(--base-opacity),
            var(--dot-2) var(--base-opacity),
            var(--dot-3) var(--base-opacity),
            var(--dot-4) var(--base-opacity),
            var(--dot-5) var(--base-opacity),
            var(--dot-6) var(--fading-opacity-2),
            var(--dot-7) var(--fading-opacity-1),
            var(--dot-8) var(--full-opacity);
    }
}
